import { useEffect, useState } from 'react'
import { ChevronRight, Copy, ImagePlus, Link2, Lock, Pencil, Plus, Settings2, Tablet as TabletIcoon, Trash2, Unlock, UserRound, X } from 'lucide-react'
import { verklein } from '../lib/afbeelding'
import { foutTekst, vraag } from '../lib/api'
import { datum, datumTijd, geleden, getal } from '../lib/opmaak'
import type { BeheerVestiging } from '../lib/typen'
import { DAGEN, DAGNAAM, alleVestigingen, euro, maakSlug, mag, type Uren } from '../../supabase/functions/_gedeeld/regels.ts'
import { Lade } from '../onderdelen/Lade'
import { ga } from '../lib/router'
import { Laden, useLaad, type Sessie } from './Beheer'

/* ------------------------------------------------------------------ *
 *  Overzicht
 * ------------------------------------------------------------------ */

interface Tel { vandaag: number; week: number; maand: number; punten: number; reeks: number[] }
interface OverzichtData {
  dagen: string[]
  perVestiging: { id: string; naam: string; slug: string; actief: boolean; geblokkeerd: boolean; scans: Tel; inwisselingen: Tel; nieuweKlanten: number }[]
  groep: { klanten: number; saldo: number; chauffeursOpen: number } | null
  waardePerPuntEuro: number
}

function Spark({ reeks }: { reeks: number[] }) {
  const max = Math.max(1, ...reeks)
  const w = 120
  const h = 32
  const stap = w / Math.max(1, reeks.length - 1)
  const d = reeks.map((n, i) => `${i ? 'L' : 'M'}${(i * stap).toFixed(1)},${(h - 2 - (n / max) * (h - 4)).toFixed(1)}`).join(' ')
  return (
    <svg className="sparkline" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden>
      <path d={d} fill="none" stroke="currentColor" strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
    </svg>
  )
}

export function Overzicht({ s }: { s: Sessie }) {
  const { data, fout, laad } = useLaad<OverzichtData>('overzicht')
  const [detail, zetDetail] = useState<{ id: string; naam: string } | null>(null)
  useEffect(() => { const t = setInterval(laad, 60_000); return () => clearInterval(t) }, [laad])
  if (!data) return <><h2 className="pagina-kop">Overzicht</h2><Laden fout={fout} opnieuw={laad} /></>
  const som = (f: (v: OverzichtData['perVestiging'][number]) => number) => data.perVestiging.reduce((a, v) => a + f(v), 0)
  const geblokkeerd = data.perVestiging.filter((v) => v.geblokkeerd)
  const lijst = [...data.perVestiging].filter((v) => v.actief).sort((a, b) => b.scans.maand - a.scans.maand)
  return (
    <>
      <div className="pagina-kop"><h2>Overzicht</h2><span className="stil klein">laatste 30 dagen</span></div>
      {geblokkeerd.length > 0 && mag(s.ik.rol, 'tablets') && (
        <div className="melding fout" style={{ marginBottom: 14 }}>
          <Lock size={19} /><span>PIN geblokkeerd op {geblokkeerd.map((v) => v.naam).join(', ')}. Vrijgeven bij Tablets.</span>
        </div>
      )}
      <div className="tegels">
        <div className="kaart tegel"><small>Scans vandaag</small><b>{getal(som((v) => v.scans.vandaag))}</b><span>{getal(som((v) => v.scans.week))} deze week</span></div>
        <div className="kaart tegel"><small>Scans 30 dagen</small><b>{getal(som((v) => v.scans.maand))}</b><span>{getal(som((v) => v.nieuweKlanten))} nieuwe tags</span></div>
        <div className="kaart tegel"><small>Ingewisseld</small><b>{getal(som((v) => v.inwisselingen.maand))}</b><span>{getal(som((v) => v.inwisselingen.punten))} punten · {euro(som((v) => v.inwisselingen.punten) * data.waardePerPuntEuro)}</span></div>
        {data.groep
          ? <div className="kaart tegel"><small>Uitstaand</small><b>{getal(data.groep.saldo)}</b><span>punten bij {getal(data.groep.klanten)} klanten · {euro(data.groep.saldo * data.waardePerPuntEuro)}</span></div>
          : <div className="kaart tegel"><small>Vestigingen</small><b>{lijst.length}</b><span>in jouw overzicht</span></div>}
      </div>
      {data.groep && data.groep.chauffeursOpen > 0 && (
        <div className="melding ok" style={{ marginBottom: 14 }}>{data.groep.chauffeursOpen} chauffeur(s) hebben de chauffeursbeloning gehaald en wachten op afhandeling.</div>
      )}
      <div className="kaart" style={{ padding: '4px 0' }}>
        <table className="tabel kaartjes raster">
          <thead><tr><th>Vestiging</th><th className="getal">Vandaag</th><th className="getal">Week</th><th className="getal">30 dagen</th><th style={{ width: 140 }}>Verloop</th><th className="getal">Ingewisseld</th><th className="getal">Nieuw</th></tr></thead>
          <tbody>
            {lijst.map((v) => (
              <tr key={v.id} className="klikbaar" onClick={() => zetDetail({ id: v.id, naam: v.naam })} title="Bekijk wie er komt">
                <td className="hoofd">
                  <span className="rij" style={{ flexWrap: 'nowrap', justifyContent: 'space-between', width: '100%' }}>
                    <span>{v.naam.replace(/^Truckwash\s*/i, '')} {v.geblokkeerd && <span className="badge dicht"><Lock size={12} />PIN</span>}</span>
                    <ChevronRight size={18} className="stil verberg-breed" />
                  </span>
                </td>
                <td className="getal" data-l="Vandaag">{v.scans.vandaag}</td>
                <td className="getal" data-l="Week">{v.scans.week}</td>
                <td className="getal" data-l="30 dagen"><b>{v.scans.maand}</b></td>
                <td className="breed-cel" data-l="Verloop"><Spark reeks={v.scans.reeks} /></td>
                <td className="getal" data-l="Ingewisseld">{v.inwisselingen.maand}</td>
                <td className="getal" data-l="Nieuwe tags">{v.nieuweKlanten}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="stil klein" style={{ marginTop: 8 }}>Tik op een vestiging om te zien wie er komt.</p>
      <Lade titel={detail ? detail.naam : ''} open={!!detail} sluit={() => zetDetail(null)} breed
        kinderen={detail && <VestigingDetail id={detail.id} />} />
    </>
  )
}

interface DetailKlant { id?: string; naam: string; code: string; nieuw: boolean }
interface DetailData {
  dagen: number; volledig: boolean
  totaal: { bezoeken: number; klanten: number; terugkerend: number; nieuw: number }
  vaste: (DetailKlant & { bezoeken: number; laatste: string })[]
  recent: (DetailKlant & { t: string })[]
}
const DETAIL_PERIODES = [{ d: 1, naam: '24 uur' }, { d: 7, naam: '7 dagen' }, { d: 30, naam: '30 dagen' }, { d: 90, naam: '90 dagen' }]

/* Wie komt er op deze vestiging: de vaste klanten en wie er recent was. */
function VestigingDetail({ id }: { id: string }) {
  const [dagen, zetDagen] = useState(7)
  const [data, zetData] = useState<DetailData | null>(null)
  const [fout, zetFout] = useState<string | null>(null)
  useEffect(() => {
    let weg = false
    zetData(null); zetFout(null)
    vraag<DetailData>('vestiging-detail', { id, dagen }).then((r) => { if (!weg) zetData(r) }).catch((e) => { if (!weg) zetFout(foutTekst(e)) })
    return () => { weg = true }
  }, [id, dagen])
  /* Zonder naam is de kaartcode de herkenning. */
  const wie = (k: DetailKlant) => k.naam || <span className="code-pil">{k.code || 'onbekend'}</span>
  const open = (k: DetailKlant) => k.id && k.code ? () => ga(`/beheer/klanten?q=${encodeURIComponent(k.code)}`) : undefined
  return (
    <div className="lijst" style={{ gap: 16 }}>
      <div className="chips" style={{ marginBottom: 0 }}>
        {DETAIL_PERIODES.map((p) => <button key={p.d} className="chip" aria-pressed={dagen === p.d} onClick={() => zetDagen(p.d)}>{p.naam}</button>)}
      </div>
      {fout && <div className="melding fout">{fout}</div>}
      {!data && !fout && <Laden fout={null} opnieuw={() => {}} />}
      {data && <>
        <div className="tegels" style={{ marginBottom: 0 }}>
          <div className="kaart tegel"><small>Bezoeken</small><b>{getal(data.totaal.bezoeken)}</b><span>wasbeurten gescand</span></div>
          <div className="kaart tegel"><small>Klanten</small><b>{getal(data.totaal.klanten)}</b><span>verschillende kaarten</span></div>
          <div className="kaart tegel"><small>Vaker geweest</small><b>{getal(data.totaal.terugkerend)}</b><span>2 keer of meer</span></div>
          <div className="kaart tegel"><small>Nieuwe kaarten</small><b>{getal(data.totaal.nieuw)}</b><span>hier geregistreerd</span></div>
        </div>

        <section>
          <div className="sectie-kop"><h2>Komen het vaakst</h2><span className="stil klein">top {data.vaste.length}</span></div>
          {data.vaste.length ? (
            <ul className="tijdlijn">
              {data.vaste.map((k, i) => (
                <li key={i} className={open(k) ? 'klikbaar' : ''} onClick={open(k)}>
                  <b className="cijfer" style={{ minWidth: 28 }}>{i + 1}.</b>
                  <span className="vul" style={{ minWidth: 0 }}>
                    <b>{wie(k)}</b>{k.nieuw && <span className="badge geel" style={{ marginLeft: 6 }}>nieuw</span>}<br />
                    <span className="stil klein">{k.naam && k.code && <><span className="code-pil">{k.code}</span> · </>}laatst {datum(k.laatste)}</span>
                  </span>
                  <span className="cijfer" style={{ whiteSpace: 'nowrap' }}><b>{getal(k.bezoeken)}</b> <span className="stil klein">keer</span></span>
                </li>
              ))}
            </ul>
          ) : <p className="stil">Niemand in deze periode.</p>}
        </section>

        <section>
          <div className="sectie-kop"><h2>Wie was er</h2><span className="stil klein">{data.recent.length === 100 ? 'laatste 100' : `${data.recent.length} bezoeken`}</span></div>
          {data.recent.length ? (
            <ul className="tijdlijn">
              {data.recent.map((k, i) => (
                <li key={i} className={open(k) ? 'klikbaar' : ''} onClick={open(k)}>
                  <b className="cijfer" style={{ minWidth: 92, whiteSpace: 'nowrap' }}>{datumTijd(k.t)}</b>
                  <UserRound size={16} className="stil" />
                  <span className="vul" style={{ minWidth: 0 }}>{wie(k)}{k.nieuw && <span className="badge geel" style={{ marginLeft: 6 }}>nieuw</span>}</span>
                  {k.naam && <span className="code-pil stil klein">{k.code}</span>}
                </li>
              ))}
            </ul>
          ) : <p className="stil">Niemand in deze periode.</p>}
        </section>
        <p className="stil klein">
          {data.volledig ? 'Tik op een klant om zijn kaart te openen. ' : 'Kaartcodes zijn half afgeschermd. '}
          Alleen de naam die de klant zelf opgaf; e-mail en telefoon staan hier niet.
        </p>
      </>}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 *  Tablets
 * ------------------------------------------------------------------ */

interface TabletRij { id: string; vestiging: string; naam: string; gemaakt: string; laatstGezien: string | null }

export function Tablets({ s }: { s: Sessie }) {
  const { data, fout, laad, zet } = useLaad<{ tablets: TabletRij[] }>('tablets')
  const [code, zetCode] = useState<{ code: string; verloopt: string; slug: string; naam: string } | null>(null)
  const [melding, zetMelding] = useState<string | null>(null)
  const [hulp, zetHulp] = useState(false)
  const mijn = s.vestigingen.filter((v) => v.actief && (alleVestigingen(s.ik.rol) || s.ik.vestigingen.includes(v.id)))

  const maak = async (v: BeheerVestiging) => {
    zetMelding(null)
    try { const r = await vraag('koppelcode-maken', { vestiging: v.id }); zetCode({ ...r, naam: v.naam }) } catch (e) { zetMelding(foutTekst(e)) }
  }
  const vrijgeven = async (v: BeheerVestiging) => {
    try { await vraag('pin-vrijgeven', { vestiging: v.id }); await s.herlaad(); zetMelding(`PIN vrijgegeven voor ${v.naam}.`) } catch (e) { zetMelding(foutTekst(e)) }
  }
  const intrekken = async (t: TabletRij) => {
    if (!confirm(`${t.naam} ontkoppelen? Die tablet kan daarna niet meer scannen tot hij opnieuw gekoppeld is.`)) return
    try { zet(await vraag('tablet-intrekken', { id: t.id })) } catch (e) { zetMelding(foutTekst(e)) }
  }

  return (
    <>
      <div className="pagina-kop"><h2>Tablets</h2><button className="knop" onClick={() => zetHulp(true)}><Settings2 size={18} />Tablet inrichten</button></div>
      <p className="stil" style={{ marginBottom: 14 }}>Een tablet op de vestiging moet één keer gekoppeld worden. Maak een koppelcode, open op de tablet het adres van de vestiging en typ de code.</p>
      {melding && <div className="melding info" style={{ marginBottom: 12 }}>{melding}</div>}
      {mag(s.ik.rol, 'vestigingen') && <TabletFotos />}
      {!data && <Laden fout={fout} opnieuw={laad} />}
      <div className="lijst">
        {mijn.map((v) => {
          const tabs = (data?.tablets ?? []).filter((t) => t.vestiging === v.id)
          return (
            <section key={v.id} className="kaart vol">
              <div className="rij" style={{ marginBottom: tabs.length || v.geblokkeerd ? 12 : 0 }}>
                <div className="vul"><b style={{ fontSize: 17 }}>{v.naam.replace(/^Truckwash\s*/i, '')}</b><br /><span className="stil klein">{location.host}/{v.slug}</span></div>
                <button className="knop klein-knop" onClick={() => maak(v)}><Link2 size={17} />Koppelcode</button>
              </div>
              {v.geblokkeerd && (
                <div className="melding fout" style={{ marginBottom: 10 }}>
                  <Lock size={19} /><span className="vul">PIN geblokkeerd na te veel foute pogingen.</span>
                  <button className="knop klein-knop" onClick={() => vrijgeven(v)}><Unlock size={16} />Vrijgeven</button>
                </div>
              )}
              {tabs.map((t) => (
                <div key={t.id} className="beloning-rij">
                  <span className="beloning-icoon"><TabletIcoon size={20} /></span>
                  <span className="vul"><b>{t.naam}</b><br /><span className="stil klein">gekoppeld {datumTijd(t.gemaakt)} · {t.laatstGezien ? `laatst actief ${geleden(t.laatstGezien)}` : 'nog niet gebruikt'}</span></span>
                  <button className="knop stil-knop rond" onClick={() => intrekken(t)} aria-label="Ontkoppelen"><Trash2 size={19} /></button>
                </div>
              ))}
              {data && !tabs.length && <p className="stil klein">Nog geen tablet gekoppeld.</p>}
            </section>
          )
        })}
      </div>
      <Lade titel="Tablet inrichten" open={hulp} sluit={() => zetHulp(false)} breed kinderen={<Inrichten />} />
      <Lade titel={`Koppelcode ${code?.naam ?? ''}`} open={!!code} sluit={() => { zetCode(null); laad() }} kinderen={code && <Koppelcode {...code} />} />
    </>
  )
}

/*
 * De foto's op de tablets: één bibliotheek voor alle tablets. Op het
 * beginscherm wisselen ze elkaar af in willekeurige volgorde. De browser
 * verkleint ze tot 1600 px: scherp op een iPad, snel geladen.
 */
function TabletFotos() {
  const { data, fout: laadFout, zet } = useLaad<{ fotos: { id: string; url: string | null }[]; max: number }>('tablet-fotos')
  const [bezig, zetBezig] = useState(false)
  const [fout, zetFout] = useState<string | null>(null)
  const stuur = async (lijf: Record<string, unknown>) => {
    zetBezig(true); zetFout(null)
    try { zet(await vraag('tablet-foto', lijf)) } catch (e) { zetFout(foutTekst(e)) } finally { zetBezig(false) }
  }
  const voegToe = async (bestanden: File[]) => {
    for (const f of bestanden) {
      try { await stuur({ data: await verklein(f, 1600) }) } catch (x) { zetFout(foutTekst(x)); return }
    }
  }
  return (
    <section className="kaart vol" style={{ marginBottom: 18 }}>
      <div className="sectie-kop" style={{ marginTop: 0 }}><h2>Foto's op de tablets</h2>{data && <span className="stil klein">{data.fotos.length} van {data.max}</span>}</div>
      <p className="stil klein" style={{ marginBottom: 10 }}>De achtergrond van het beginscherm op alle tablets. Ze wisselen elkaar af in willekeurige volgorde. Liggende foto's werken het best.</p>
      {laadFout && <div className="melding fout">{laadFout}</div>}
      {data && (
        <div className="foto-raster">
          {data.fotos.map((f) => (
            <div key={f.id} className="foto-vak">
              {f.url && <img src={f.url} alt="" loading="lazy" />}
              <button type="button" className="knop rond" aria-label="Foto weghalen" disabled={bezig}
                onClick={() => { if (confirm('Deze foto weghalen van alle tablets?')) stuur({ weg: f.id }) }}><X size={16} /></button>
            </div>
          ))}
          {data.fotos.length < data.max && (
            <label className="foto-vak nieuw" style={{ cursor: bezig ? 'wait' : 'pointer' }}>
              <ImagePlus size={26} /><span>{bezig ? 'Bezig…' : "Foto's toevoegen"}</span>
              <input type="file" accept="image/*" multiple hidden disabled={bezig} onChange={async (e) => {
                const lijst = [...(e.target.files ?? [])].slice(0, data.max - data.fotos.length)
                e.target.value = ''
                await voegToe(lijst)
              }} />
            </label>
          )}
        </div>
      )}
      {fout && <div className="melding fout" style={{ marginTop: 10 }}>{fout}</div>}
    </section>
  )
}

/* Kort, voor ter plekke; de lange versie staat in docs/tablet-inrichten.md. */
function Inrichten() {
  const [toestel, zet] = useState<'ipad' | 'android'>(/iPad|Macintosh/.test(navigator.userAgent) ? 'ipad' : 'android')
  const stap = (n: number, tekst: React.ReactNode) => (
    <li className="beloning-rij" style={{ alignItems: 'flex-start' }}>
      <span className="beloning-icoon" style={{ fontWeight: 800, width: 36, height: 36 }}>{n}</span>
      <span className="vul" style={{ paddingTop: 6 }}>{tekst}</span>
    </li>
  )
  return (
    <div className="lijst" style={{ gap: 12 }}>
      <p className="stil">Een website kan een tablet niet zelf vastzetten of laten opstarten in de app. Dat doe je één keer met de instellingen van de tablet. De code om het vastzetten op te heffen is een code van het apparaat: kies er een die alleen managers kennen.</p>
      <div className="chips">
        <button className="chip" aria-pressed={toestel === 'ipad'} onClick={() => zet('ipad')}>iPad</button>
        <button className="chip" aria-pressed={toestel === 'android'} onClick={() => zet('android')}>Android</button>
      </div>
      <ol className="tijdlijn" style={{ margin: 0 }}>
        {toestel === 'ipad' ? <>
          {stap(1, <>Open in <b>Safari</b> het adres van de vestiging, dan <b>Deel → Zet op beginscherm</b>. Open TW1 Club vanaf het icoon en koppel met een koppelcode.</>)}
          {stap(2, <><b>Instellingen → Beeldscherm en helderheid → Automatisch vergrendelen → Nooit</b>.</>)}
          {stap(3, <><b>Instellingen → Toegankelijkheid → Begeleide toegang</b> aan, en een <b>toegangscode</b> instellen.</>)}
          {stap(4, <>Open TW1 Club en druk <b>drie keer snel op de zijknop</b> → <b>Start</b>. Stoppen: weer drie keer drukken plus de code. Na een herstart opnieuw starten.</>)}
          {stap(5, <><b>Kom je er niet uit</b> (code vergeten, reageert niet)? Herstart de iPad geforceerd: volume omhoog kort, volume omlaag kort, dan de bovenste knop vasthouden tot het Apple-logo (met thuisknop: thuisknop + bovenste knop vasthouden). Daarna een nieuwe code instellen bij Begeleide toegang.</>)}
        </> : <>
          {stap(1, <>Open in <b>Chrome</b> het adres van de vestiging, dan <b>⋮ → App installeren</b>. Open TW1 Club vanaf het icoon en koppel met een koppelcode.</>)}
          {stap(2, <>Zoek in Instellingen op <b>vastzetten</b>: <b>App vastzetten</b> aan, met <b>pincode vragen voor losmaken</b>.</>)}
          {stap(3, <>Open <b>Recente apps</b> → tik op het icoon boven TW1 Club → <b>Vastzetten</b>. Losmaken: Terug + Recente apps ingedrukt houden.</>)}
          {stap(4, <>Wil je dat hij na het aanzetten vanzelf in TW1 Club start: gebruik een kiosk-app als <b>Fully Kiosk Browser</b> (Start on Boot, Kiosk Mode met PIN).</>)}
        </>}
      </ol>
      <p className="stil klein">In de app zelf: het scherm blijft aan, na een minuut verschijnt een screensaver, en het medewerkersmenu open je door anderhalve seconde op het logo te drukken (daarna je PIN).</p>
    </div>
  )
}

function Koppelcode({ code, verloopt, slug }: { code: string; verloopt: string; slug: string }) {
  const [nu, zetNu] = useState(Date.now())
  useEffect(() => { const t = setInterval(() => zetNu(Date.now()), 1000); return () => clearInterval(t) }, [])
  const over = Math.max(0, Math.round((new Date(verloopt).getTime() - nu) / 1000))
  const adres = `${location.origin}/${slug}`
  return (
    <div className="lijst" style={{ gap: 14 }}>
      <ol style={{ margin: 0, paddingLeft: 20, display: 'grid', gap: 6 }}>
        <li>Open op de tablet <b>{adres.replace(/^https?:\/\//, '')}</b></li>
        <li>Op een iPad: eerst <i>Deel → Zet op beginscherm</i>, en open hem vanaf het beginscherm</li>
        <li>Typ deze code:</li>
      </ol>
      <div className="koppelcode">{code}</div>
      <p className="stil" style={{ textAlign: 'center' }}>
        {over > 0 ? `Nog ${Math.ceil(over / 60)} ${Math.ceil(over / 60) === 1 ? 'minuut' : 'minuten'} geldig, één keer te gebruiken.` : 'Verlopen. Maak een nieuwe.'}
      </p>
      <button className="knop breed" onClick={() => navigator.clipboard?.writeText(adres)}><Copy size={18} />Adres kopiëren</button>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 *  Vestigingen
 * ------------------------------------------------------------------ */

export function Vestigingen({ s }: { s: Sessie }) {
  const [bewerk, zetBewerk] = useState<Partial<BeheerVestiging> | null>(null)
  return (
    <>
      <div className="pagina-kop">
        <h2>Vestigingen</h2>
        <button className="knop geel" onClick={() => zetBewerk({ actief: true, uren: null })}><Plus size={19} />Nieuwe vestiging</button>
      </div>
      <div className="kaart" style={{ padding: '4px 0' }}>
        <table className="tabel kaartjes">
          <thead><tr><th>Vestiging</th><th>Adres</th><th>Tablet-adres</th><th>Status</th><th /></tr></thead>
          <tbody>
            {s.vestigingen.map((v) => (
              <tr key={v.id} className="klikbaar" onClick={() => zetBewerk(v)}>
                <td className="hoofd">{v.naam}</td>
                <td data-l="Adres">{v.adres ? `${v.adres}, ${v.plaats}` : <span className="stil">nog niet bekend</span>}</td>
                <td data-l="Tablet">/{v.slug}</td>
                <td data-l="Status">
                  {v.actief ? <span className="badge open">Actief</span> : <span className="badge">Uit</span>}{' '}
                  {!v.uren && <span className="badge let">Geen tijden</span>}{' '}
                  {(v.lat === null || v.lon === null) && <span className="badge let">Geen locatie</span>}
                </td>
                <td data-l="" className="verberg-mobiel"><Pencil size={17} className="stil" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Lade titel={bewerk?.id ? bewerk.naam ?? '' : 'Nieuwe vestiging'} open={!!bewerk} sluit={() => zetBewerk(null)} breed
        kinderen={bewerk && <VestigingFormulier v={bewerk} klaar={async () => { zetBewerk(null); await s.herlaad() }} />} />
    </>
  )
}

function VestigingFormulier({ v, klaar }: { v: Partial<BeheerVestiging>; klaar: () => void }) {
  const [f, zet] = useState({
    naam: v.naam ?? '', slug: v.slug ?? '', adres: v.adres ?? '', postcode: v.postcode ?? '', plaats: v.plaats ?? '',
    telefoon: v.telefoon ?? '', lat: v.lat?.toString() ?? '', lon: v.lon?.toString() ?? '', reviewUrl: v.reviewUrl ?? '', actief: v.actief !== false,
  })
  const [uren, zetUren] = useState<Uren>(v.uren ?? {})
  const [fout, zetFout] = useState<string | null>(null)
  const [bezig, zetBezig] = useState(false)
  const veld = (k: keyof typeof f) => ({ value: String(f[k]), onChange: (e: { target: { value: string } }) => zet({ ...f, [k]: e.target.value }) })
  const opslaan = async () => {
    zetBezig(true); zetFout(null)
    try {
      const heeftUren = DAGEN.some((d) => uren[d])
      await vraag('vestiging-opslaan', { id: v.id, ...f, lat: f.lat.replace(',', '.'), lon: f.lon.replace(',', '.'), uren: heeftUren ? uren : null })
      klaar()
    } catch (e) { zetFout(foutTekst(e)) } finally { zetBezig(false) }
  }
  return (
    <form className="formulier" onSubmit={(e) => { e.preventDefault(); opslaan() }}>
      <div className="twee-kol">
        <label className="veld"><span>Naam</span><input {...veld('naam')} required /></label>
        <label className="veld"><span>Tablet-adres</span><input {...veld('slug')} placeholder={maakSlug(f.naam) || 'roosendaal'} autoCapitalize="none" />
          <small>{location.host}/{f.slug || maakSlug(f.naam)}</small></label>
        <label className="veld"><span>Straat en nummer</span><input {...veld('adres')} autoComplete="off" /></label>
        <div className="twee-kol" style={{ gridTemplateColumns: '1fr 1.4fr' }}>
          <label className="veld"><span>Postcode</span><input {...veld('postcode')} /></label>
          <label className="veld"><span>Plaats</span><input {...veld('plaats')} /></label>
        </div>
        <label className="veld"><span>Telefoon</span><input {...veld('telefoon')} inputMode="tel" /></label>
        <label className="veld" style={{ gridColumn: '1 / -1' }}><span>Google-reviewlink</span>
          <input {...veld('reviewUrl')} type="url" inputMode="url" placeholder="https://g.page/r/.../review" autoCapitalize="none" />
          <small>Voor de mail "Welkom terug". In Google Bedrijfsprofiel: <b>Vraag om reviews</b> → link kopiëren.</small></label>
        <div className="twee-kol">
          <label className="veld"><span>Breedtegraad</span><input {...veld('lat')} inputMode="decimal" placeholder="51.55" /></label>
          <label className="veld"><span>Lengtegraad</span><input {...veld('lon')} inputMode="decimal" placeholder="4.46" /></label>
        </div>
      </div>
      <div className="veld">
        <span>Openingstijden</span>
        <div className="uren-raster">
          {DAGEN.map((d) => {
            const vak = uren[d]
            return (
              <div key={d} className="dag">
                <b style={{ textTransform: 'capitalize' }}>{d}</b>
                <label className="vink" style={{ padding: 0, alignItems: 'center' }}>
                  <input type="checkbox" checked={!!vak} onChange={(e) => zetUren({ ...uren, [d]: e.target.checked ? { van: '08:00', tot: '18:00' } : null })} />
                  <span className="klein">Open</span>
                </label>
                {vak ? <>
                  <input type="time" value={vak.van} onChange={(e) => zetUren({ ...uren, [d]: { ...vak, van: e.target.value } })} aria-label={`${DAGNAAM[d]} van`} />
                  <input type="time" value={vak.tot} onChange={(e) => zetUren({ ...uren, [d]: { ...vak, tot: e.target.value } })} aria-label={`${DAGNAAM[d]} tot`} />
                </> : <span className="stil" style={{ gridColumn: 'span 2' }}>gesloten</span>}
              </div>
            )
          })}
        </div>
      </div>
      <label className="vink"><input type="checkbox" checked={f.actief} onChange={(e) => zet({ ...f, actief: e.target.checked })} /><span>Actief (zichtbaar voor klanten, tablets werken)</span></label>
      {fout && <div className="melding fout">{fout}</div>}
      <div className="knoppenrij"><button className="knop geel" disabled={bezig}>{bezig ? 'Opslaan…' : 'Opslaan'}</button></div>
    </form>
  )
}
